import { useState } from "react";
import { Mail, MapPin, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [ok, setOk] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const d = await api.post("/contact", form);
      setOk(d.data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const F = (key, label, testid, type = "text", extra = {}) => (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{label}</span>
      <input
        data-testid={testid}
        type={type}
        required
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-obsidian border border-gold/15 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-gold/50"
        {...extra}
      />
    </label>
  );

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-32 pb-24">
      <PageHeader
        eyebrow={t("contact.eyebrow")}
        title={<>{t("contact.title1")} <span className="italic normal-case text-gold">{t("contact.title2")}</span></>}
        sub={t("contact.sub")}
      />

      <div className="grid lg:grid-cols-[2fr_1fr] gap-12">
        <form data-testid="contact-form" onSubmit={submit} className="border border-gold/15 bg-ink p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            {F("name", t("contact.name"), "contact-name-input", "text", { minLength: 2 })}
            {F("email", t("contact.email"), "contact-email-input", "email")}
          </div>
          {F("subject", t("contact.subject"), "contact-subject-input", "text", { minLength: 3 })}
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{t("contact.message")}</span>
            <textarea
              data-testid="contact-message-input"
              rows={5}
              required
              minLength={10}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-obsidian border border-gold/15 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-gold/50"
            />
          </label>
          {ok && <p data-testid="contact-success" className="text-xs text-emerald-300">{ok}</p>}
          {error && <p data-testid="contact-error" className="text-xs text-red-400">{error}</p>}
          <button type="submit" data-testid="contact-submit-button" disabled={busy} className="bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase px-10 py-4 hover:bg-gold-light disabled:opacity-40 transition-colors">
            {busy ? t("contact.sent") : t("contact.send")}
          </button>
        </form>

        <div className="space-y-6">
          {[
            [<Mail size={15} key="m" />, t("contact.emailLabel"), "klantenservice@deoatelier.nl"],
            [<MessageCircle size={15} key="w" />, t("contact.waLabel"), t("contact.waValue")],
            [<MapPin size={15} key="p" />, t("contact.shipLabel"), t("contact.shipValue")],
          ].map(([icon, label, value]) => (
            <div key={label} className="flex items-start gap-4 border border-gold/10 bg-ink p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold shrink-0">{icon}</span>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">{label}</p>
                <p className="text-sm text-cream mt-1">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Contact;
